import { Fragment } from 'react';

import styled from 'styled-components';

import { Alert } from '@components/alert';
import { Card } from '@components/card';
import { ExpandableText } from '@components/expandable-text';
import { UploadedFile } from '@components/file-field';
import { createForm, useCreateFormStore } from '@components/form';
import { HideContainer } from '@components/hide-container';
import { IconFormIndicatorError } from '@components/icons/form-indicator-error';
import { IconSend } from '@components/icons/send';
import { InfoBox } from '@components/info-box';
import { useIntegrationEndpoint } from '@components/integrations';
import { IntegrationIcon } from '@components/integrations/common';
import {
  IntegrationEndpoint,
  IntegrationEndpointVariable,
} from '@components/integrations/integrations-types';
import { LabelTextGroup } from '@components/label-text-group';
import { RelativeSpinner, Spinner } from '@components/loading-spinner';
import { usePreviewFileModal } from '@components/modals/preview-file-modal';
import { SelectOption } from '@components/select';
import { Separator } from '@components/separator';
import { Sidebar } from '@components/sidebar';
import { Stack } from '@components/stack';
import { Text } from '@components/text';
import { VariableValue } from '@components/text-editors/addons/field-mapping';
import { useScopedTranslate } from '@locale/translate';
import { abbreviateDate } from '@utils/date';
import { fileNameFromPath, isBase64File } from '@utils/strings';

import { useMarketplaceTemplate } from 'api/app-marketplace/hooks';
import { createTravellerJourneyActionDocumentPath } from 'api/documents/requests';
import { useUploadFile } from 'api/files/hooks';
import {
  useIntegrationTravellerActionFile,
  useTravellerActionIntegrationEndpoint,
} from 'api/integrations/hooks';
import { FormFile } from 'api/journey-action-forms/requests';
import {
  useActivityLogs,
  useJourneyTravellerAction,
  useUpdateJourneyTravellerAction,
} from 'api/traveller-actions/hooks';
import { TravellerJourneyActionDetailed } from 'api/types';
import { ExecutionMethod } from 'ui/journey-builder/execution-method';
import { DescriptionFormField, useVariableGroups } from 'ui/journey-builder/node-types/common';
import { FormWithUnsavedChangesPrompt } from 'ui/journey-builder/unsaved-changes-prompt';

import { AppNotConnectedSection, DisabledSection, SidebarWarningSection } from './common';
import { IntegrationField, processVariableValue } from './journey-sidebar';

import { ACTION_TYPES } from '../action-config';
import {
  ActionProgressBar,
  Base64DownlodableLink,
  FormSidebarFooter,
  isActionError,
  SidebarProps,
  TravellerActionActions,
  TravellerActionFooter,
  TravellerActionHeader,
  TriggerMethod,
  useActionTabs,
  useEditingContext,
} from '../common';
import { getFieldMappingId } from '../helpers';
import { extractRequestHeaders } from '../shared/field-utils';

const ACTION_TYPE = ACTION_TYPES.integration;

export const SidebarContent = ({ shortId }: SidebarProps) => {
  const { t } = useScopedTranslate(sidebarLocale);
  const travellerActionQuery = useJourneyTravellerAction<typeof ACTION_TYPE>(shortId);
  const {
    data: integration,
    isLoading: endpointIsLoading,
    isError: endpointIsError,
  } = useTravellerActionIntegrationEndpoint(shortId);
  const templateId = integration?.templateId;
  // If this is a template endpoint, fetch the template to check connection status
  const { data: template } = useMarketplaceTemplate(templateId ?? '');
  const hasAuthError = Boolean(templateId && template?.connectionStatus !== 'connected');
  const { data: activityLogs } = useActivityLogs(shortId);
  const activityCount = activityLogs?.length ?? 0;
  const action = travellerActionQuery.data;

  const wrapContent = (content: JSX.Element) => {
    const Container = hasAuthError ? DisabledSection : Fragment;
    return endpointIsError ? (
      <InfoBox variant="red" icon={<IconFormIndicatorError />} text={t('integrationError')} />
    ) : (
      <>
        {hasAuthError && (
          <SidebarWarningSection>
            <AppNotConnectedSection template={template} travellerActionId={shortId} />
          </SidebarWarningSection>
        )}
        <Container>{content}</Container>
      </>
    );
  };

  const { tabs } = useActionTabs({
    actionId: shortId ?? '',
    activityCount,
    display: action ? (
      wrapContent(<DetailsContent action={action} hideFooter={hasAuthError} />)
    ) : (
      <></>
    ),
    edit: action ? wrapContent(<FormContent action={action} hideFooter={hasAuthError} />) : <></>,
  });

  if (travellerActionQuery.isLoading || endpointIsLoading) {
    return <RelativeSpinner />;
  }
  if (!shortId || !action) {
    return <div>{t('dataNotFound')}</div>;
  }

  return (
    <Sidebar>
      <TravellerActionHeader
        actionType={ACTION_TYPE}
        title={integration?.name || undefined}
        customIcon={
          integration?.thumbnail ? <IntegrationIcon src={integration.thumbnail} /> : undefined
        }
        subtitle={integration?.integrationName || undefined}
        subtitleFont="regular12"
        subtitleLocation="below"
        tabs={tabs}
      >
        <TravellerActionActions action={action} />
      </TravellerActionHeader>
    </Sidebar>
  );
};

type ContentProps = {
  action: TravellerJourneyActionDetailed<typeof ACTION_TYPE>;
  hideFooter?: boolean;
};

const DetailsContent = ({ action, hideFooter }: ContentProps) => {
  const { t } = useScopedTranslate(sidebarLocale);
  const { data, isLoading } = useIntegrationEndpoint(
    action.values?.integrationEndpointId.value ?? ''
  );
  const isError = isActionError(action);

  if (isLoading || !data) {
    return <Spinner />;
  }

  const variables = data.variables.filter((variable) => variable.visible);

  const hasError = action.status !== 'completed' && action.result?.status === 'error';

  return (
    <Stack space={6}>
      <ActionProgressBar action={action} isError={isError} isCard={false} />
      <Sidebar.Separator />
      <HideContainer title={t('actionDetails')}>
        <Stack space={4}>
          <ExpandableText text={data?.description} />
          <LabelTextGroup label={t('description')} text={action.description} />
        </Stack>
      </HideContainer>
      {hasError && (
        <>
          <Sidebar.Separator />
          <HideContainer title={t('errorResponse')}>
            <Alert variant="error">
              <CodeBlock>{JSON.stringify(action.result?.validationError, null, 2)}</CodeBlock>
            </Alert>
          </HideContainer>
        </>
      )}
      {variables.length > 0 && (
        <>
          <Sidebar.Separator />
          <HideContainer title={t('requestData')}>
            <Stack space={4}>
              {action.status === 'completed' ? (
                <CompletedContent action={action} data={data} />
              ) : (
                <IncompleteContent action={action} data={data} />
              )}
            </Stack>
          </HideContainer>
        </>
      )}
      {action.status === 'completed' && data.responseValues && data.responseValues.length > 0 && (
        <>
          <Sidebar.Separator />
          <HideContainer title={t('responseData')}>
            <Stack space={4}>
              <ResponseDataContent action={action} data={data} />
            </Stack>
          </HideContainer>
        </>
      )}
      {!hideFooter && (
        <TravellerActionFooter
          action={action}
          startIcon={<IconSend />}
          startText={t('run')}
          retryText={t('retry')}
        />
      )}
    </Stack>
  );
};

type ArrayFieldValue = Array<Record<string, string | ArrayFieldValue>>;

const RenderArrayField = ({
  field,
  value,
}: {
  field: IntegrationEndpointVariable;
  value?: ArrayFieldValue;
}) => {
  const showFilePreviewModal = usePreviewFileModal();
  if (!value) {
    return <LabelTextGroup key={field.name} label={field.label} />;
  }
  return (
    <Stack space={2}>
      <Text font="semibold14" key={field.name} text={field.label} />
      {(value ?? []).map((item: Record<string, string | ArrayFieldValue | FormFile>, idx) => (
        <Card space={4} key={idx}>
          <Stack space={2}>
            {(field.template ?? [])
              .filter((template) => template.visible)
              .map((template) => {
                if (template.type === 'array') {
                  return (
                    <RenderArrayField
                      key={template.name}
                      field={template}
                      value={item[template.name] as ArrayFieldValue}
                    />
                  );
                }
                if (template.type === 'file' && item[template.name]) {
                  const data = item[template.name];
                  const isObject = typeof data === 'object';
                  const file: FormFile = isObject
                    ? (data as FormFile)
                    : {
                        name: fileNameFromPath(data as string),
                        src: data as string,
                      };
                  return (
                    <UploadedFile
                      name={file.name}
                      path={file.src}
                      onPreview={() => showFilePreviewModal({ formFile: file, filesList: [file] })}
                      isDisabled={!isObject}
                    />
                  );
                }
                return (
                  <LabelTextGroup
                    key={template.name}
                    label={template.label}
                    text={item[template.name] as string}
                  />
                );
              })}
          </Stack>
        </Card>
      ))}
    </Stack>
  );
};

const getRequestDataText = (
  action: TravellerJourneyActionDetailed<'integration'>,
  field: IntegrationEndpointVariable
) => {
  const value = action.data.requestData[field.name];
  const display =
    field.type === 'select' && field.options
      ? field.options.find((option) => value === option.value)?.label ?? value
      : field.type === 'boolean'
      ? String(value ?? false)
      : value;
  return field.type === 'date' && value ? abbreviateDate(value, 'en') : display;
};

type ExtendedContentProps = ContentProps & {
  data: IntegrationEndpoint;
};

const CompletedContent = ({ action, data }: ExtendedContentProps) => {
  const showFilePreviewModal = usePreviewFileModal();
  const variables = data.variables.filter((variable) => variable.visible);

  return (
    <>
      {variables.map((field) => {
        const value = action.data.requestData[field.name];
        if (field.type === 'array') {
          return <RenderArrayField key={field.name} field={field} value={value} />;
        }
        return (
          <Fragment key={field.name}>
            {isBase64File(value) ? (
              <div>
                <Text font="semibold14" text={field.label} />
                <Base64DownlodableLink base64string={value} showAsCard />
              </div>
            ) : typeof value === 'object' && value?.src ? (
              <UploadedFile
                name={value.name ?? fileNameFromPath(value.src)}
                path={value.src}
                iconColor="elementBlue100"
                font="regular14"
                onPreview={() => {
                  showFilePreviewModal({
                    formFile: {
                      src: value.src,
                      name: value.name ?? fileNameFromPath(value.src),
                    },
                    filesList: [
                      { src: value.src, name: value.name ?? fileNameFromPath(value.src) },
                    ],
                  });
                }}
              />
            ) : (
              <LabelTextGroup
                key={field.name}
                label={field.label}
                text={getRequestDataText(action, field)}
              />
            )}
          </Fragment>
        );
      })}
      <RequestHeadersDisplay action={action} data={data} />
    </>
  );
};

const ResponseDataContent = ({ action, data }: ExtendedContentProps) => {
  const showFilePreviewModal = usePreviewFileModal();
  const responseData = action.data.responseData;
  const hasNumericFileId = typeof responseData?.file === 'number';
  const fileQuery = useIntegrationTravellerActionFile(hasNumericFileId ? action.shortId : '');
  const fetchedFilePath = fileQuery.data?.file;
  const fallbackFilePath = hasNumericFileId ? fetchedFilePath : responseData?.file;
  const showFallbackFile = !data.responseValues && responseData?.file && fallbackFilePath;
  const fallbackFileName = fallbackFilePath ? fileNameFromPath(fallbackFilePath) : '';

  return (
    <>
      {data.responseValues?.map((field) => {
        const fieldValue = responseData
          ? responseData[field.name]
          : // Backwards compatibility
            action.data[field.name];

        if (field.type === 'file' && fieldValue) {
          const isObject = typeof fieldValue === 'object';
          const isNumericId = typeof fieldValue === 'number';

          const resolvedFilePath = isNumericId ? fetchedFilePath : (fieldValue as string);

          if (isNumericId && !fetchedFilePath) {
            return (
              <Stack space={2} key={field.name}>
                <Text font="semibold14" text={field.label} />
              </Stack>
            );
          }

          const fileData: FormFile = isObject
            ? (fieldValue as FormFile)
            : {
                name: fileNameFromPath(resolvedFilePath ?? ''),
                src: resolvedFilePath ?? '',
              };

          return (
            <Stack space={2} key={field.name}>
              <Text font="semibold14" text={field.label} />
              <UploadedFile
                name={fileData.name}
                path={fileData.src}
                onPreview={() =>
                  showFilePreviewModal({
                    formFile: fileData,
                    filesList: [fileData],
                  })
                }
                iconColor="elementPurpleInvert"
                font="regular14"
              />
            </Stack>
          );
        }

        return (
          <LabelTextGroup
            key={field.name}
            label={field.label}
            text={
              typeof field !== 'object'
                ? action.data[field]
                : fieldValue
                ? JSON.stringify(fieldValue)
                : ''
            }
          />
        );
      })}
      {showFallbackFile && (
        <Stack space={2}>
          <UploadedFile
            name={fallbackFileName}
            path={fallbackFilePath}
            onPreview={() =>
              showFilePreviewModal({
                formFile: {
                  name: fallbackFileName,
                  src: fallbackFilePath,
                },
                filesList: [
                  {
                    name: fallbackFileName,
                    src: fallbackFilePath,
                  },
                ],
              })
            }
            iconColor="elementPurpleInvert"
            font="regular14"
          />
        </Stack>
      )}
    </>
  );
};

const IncompleteContent = ({ action, data }: ExtendedContentProps) => {
  const { t } = useScopedTranslate(sidebarLocale);
  const showFilePreviewModal = usePreviewFileModal();
  const variables = data.variables.filter((variable) => variable.visible);
  return (
    <>
      {variables.map((field) => {
        if (field.type === 'array') {
          return (
            <RenderArrayField
              key={field.name}
              field={field}
              value={action.data.requestData[field.name]}
            />
          );
        }
        if (field.type === 'file') {
          const file = action.data.requestData[field.name];
          return (
            <Stack space={2} key={field.name}>
              <Text font="semibold14" text={field.label} />
              {file ? (
                <UploadedFile
                  name={file.name ?? fileNameFromPath(file)}
                  path={file.src ?? ''}
                  onPreview={
                    file.src
                      ? () => {
                          showFilePreviewModal({
                            formFile: file,
                            filesList: [file],
                          });
                        }
                      : undefined
                  }
                  iconColor="elementBlue100"
                  font="regular14"
                />
              ) : (
                <Text font="regular14" color="textHelper" text={t('core.not_set')} />
              )}
            </Stack>
          );
        }
        return (
          <LabelTextGroup
            key={field.name}
            label={field.label}
            text={getRequestDataText(action, field)}
          />
        );
      })}
      <RequestHeadersDisplay action={action} data={data} />
    </>
  );
};

const RequestHeadersDisplay = ({ action, data }: ExtendedContentProps) => {
  const { t } = useScopedTranslate(sidebarLocale);
  const requestHeaders = (data.requestHeaders ?? []).filter((h) => h.visible);
  const headerData = action.data.requestHeaders ?? {};

  if (requestHeaders.length === 0) return null;

  return (
    <>
      <Text font="heading2" text={t('requestHeaders')} />
      {requestHeaders.map((header) => {
        const raw = headerData[header.name];
        const text =
          typeof raw === 'object' && raw !== null && 'value' in raw
            ? String((raw as { value: unknown }).value)
            : String(raw ?? '');
        return <LabelTextGroup key={header.name} label={header.label} text={text} />;
      })}
    </>
  );
};

const CodeBlock = styled.pre(() => ({
  'word-wrap': 'break-word',
  'white-space': 'pre-wrap',
}));

type IntegrationData = {
  id: string;
  description: string;
  isAutomatic: boolean;
  assignees: SelectOption[];
  executionMethod: TriggerMethod;
  integrationId: string;
} & Record<string, { value: string; variables: object } | string>;

const Form = createForm<IntegrationData>();

const FormContent = ({ action, hideFooter }: ContentProps) => {
  const { t } = useScopedTranslate(sidebarLocale);
  const { stopEditing } = useEditingContext();
  const { data, isLoading } = useIntegrationEndpoint(action.data.integrationEndpointId);
  const variables = (data?.variables ?? []).filter((variable) => variable.visible);
  const requestHeaders = (data?.requestHeaders ?? []).filter((h) => h.visible);

  const formStore = useCreateFormStore();
  const isDirty = formStore.useIsDirty();
  const editMutation = useUpdateJourneyTravellerAction(action, {
    onSuccess: stopEditing,
  });
  const uploadMutation = useUploadFile(createTravellerJourneyActionDocumentPath(action), true);
  const fieldMappingId = getFieldMappingId(action.journeyId);

  const extractRequestData = (data: IntegrationData) => {
    const prefix = 'requestData__';
    const requestData: { [key: string]: any } = {};

    const setArrayValues = (obj: any, path: (string | number)[], value: any) => {
      let current = obj;
      for (let i = 0; i < path.length - 1; i++) {
        const part = path[i];
        if (!current[part]) {
          if (isNaN(Number(path[i + 1]))) {
            current[part] = {};
          } else {
            current[part] = [];
          }
        }
        current = current[part];
      }
      current[path[path.length - 1]] = value;
    };

    Object.keys(data).forEach((key) => {
      if (key.startsWith(prefix)) {
        const newKey = key.substring(prefix.length);
        const value = data[key];
        if (value === undefined) return;
        let processedValue = value;
        if (typeof value !== 'object') {
          processedValue = { value, variables: {} };
        }
        const variable = variables.find((v) => v.name === newKey);
        if (variable) {
          if (typeof processedValue === 'object') {
            processedValue = processVariableValue(
              variable,
              processedValue
            ) as typeof processedValue;
          }
          requestData[newKey] = processedValue;
        } else {
          const path = newKey
            .split('__')
            .map((part) => (isNaN(Number(part)) ? part : Number(part)));
          setArrayValues(requestData, path, processedValue);
        }
      }
    });

    return requestData;
  };

  const handleSave = async (data: IntegrationData) => {
    await editMutation.mutateAsync({
      description: data.description,
      isAutomatic: data.executionMethod === 'automatic',
      action: 'integration',
      assignees: data.executionMethod === 'manual' ? data.assignees.map((user) => user.value) : [],
      values: {
        integrationEndpointId: { value: action.data.integrationEndpointId },
        requestData: extractRequestData(data),
        requestHeaders: extractRequestHeaders(data),
        continueOnError: data.continueOnError,
      },
    });
  };

  const variableGroups = useVariableGroups({
    actionVariables: action.actionVariables,
    journeyId: action.journeyId,
  });
  const emailVariableGroups = useVariableGroups({
    actionVariables: action.actionVariables,
    journeyId: action.journeyId,
    types: ['email'],
  });
  const dateVariableGroups = useVariableGroups({
    actionVariables: action.actionVariables,
    journeyId: action.journeyId,
    types: ['date'],
  });
  const optionsVariableGroups = useVariableGroups({
    actionVariables: action.actionVariables,
    journeyId: action.journeyId,
    types: ['select', 'multi_select'],
  });

  const getVariableGroups = (fieldType: string) => {
    switch (fieldType) {
      case 'email':
        return emailVariableGroups;
      case 'date':
        return dateVariableGroups;
      case 'select':
        return optionsVariableGroups;
      default:
        return variableGroups;
    }
  };

  return (
    <FormWithUnsavedChangesPrompt store={formStore} onSubmit={(data) => handleSave(data)}>
      <Stack orientation="vertical" space={6}>
        <HideContainer title={t('actionDetails')}>
          <Stack space={4}>
            <ExpandableText text={data?.description} />
            <DescriptionFormField defaultValue={action.description} />
          </Stack>
        </HideContainer>
        <Sidebar.Separator />
        <HideContainer title={t('triggerMethod')}>
          <ExecutionMethod
            assignees={action.assignees}
            isAutomatic={action.isAutomatic}
            formStore={formStore}
            hideLabel
          />
        </HideContainer>
        {variables.length > 0 && (
          <>
            <Sidebar.Separator />
            <HideContainer title={t('requestValues')}>
              <Stack space={4}>
                {isLoading && <Spinner />}
                {variables.map((field) => (
                  <IntegrationField
                    key={field.name}
                    field={field}
                    allFields={variables}
                    defaultValue={action.values?.requestData?.[field.name]}
                    variableGroups={getVariableGroups(field.type)}
                    uploadMutation={uploadMutation}
                    fieldMappingId={fieldMappingId}
                  />
                ))}
                {requestHeaders.length > 0 && (
                  <>
                    <Separator />
                    <Text font="bold14" text={t('requestHeaders')} />
                    {requestHeaders.map((header) => (
                      <Form.FieldMappingTextEditor
                        key={header.name}
                        id={`requestHeaders__${header.name}`}
                        label={header.label}
                        defaultValue={
                          action.values?.requestHeaders?.[header.name] ??
                          (header.default_value
                            ? ({ value: header.default_value } as VariableValue)
                            : undefined)
                        }
                        variableGroups={variableGroups}
                        singleLine
                        plaintext
                        fieldMappingId={fieldMappingId}
                      />
                    ))}
                  </>
                )}
              </Stack>
            </HideContainer>
          </>
        )}
        <Sidebar.Separator />
        <HideContainer title={t('advanced')} defaultClosed>
          <Form.Checkbox
            // @ts-ignore
            id="continueOnError"
            label={t('continueOnError')}
            defaultValue={action.values?.continueOnError ?? false}
          />
        </HideContainer>
      </Stack>
      {!hideFooter && (
        <FormSidebarFooter Form={Form} isDirty={isDirty} editMutation={editMutation} />
      )}
    </FormWithUnsavedChangesPrompt>
  );
};
const sidebarLocale = {
  en: {
    title: 'Integration',
    dataNotFound: 'Data not found',
    description: 'Description',
    automatic: 'Automatic',
    manual: 'Manual',
    details: 'Details',
    file: 'File',
    started: 'Started',
    completed: 'Completed',
    run: 'Run',
    retry: 'Retry',
    template: 'Template',
    inProgress: 'In progress',
    error: 'Error',
    notStarted: 'Not started',
    requestData: 'Request data',
    responseData: 'Response data',
    errorResponse: 'Error response',
    addMore: 'Add more',
    requestHeaders: 'Request Headers',
    integrationError:
      'Failed to load integration, if the integration has been deleted please replace the action in the journey builder',
    advanced: 'Advanced',
    continueOnError: 'Continue on error',
    actionDetails: 'Action details',
    triggerMethod: 'Trigger method',
    requestValues: 'Request values',
  },
};
